import React, { useMemo, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, TextField, MenuItem, Button, Autocomplete,
    FormControlLabel, Checkbox
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const containerType = [
    { label: "20'DC" },
    { label: "40'DC" },
    { label: "40'HC" },
    { label: "45'HC" },
    { label: "20'Open-Top" },
    { label: "40'Open-Top" },
    { label: "20'Flatrack" },
    { label: "40'Flatrack" },
    { label: "20'Reefer" },
    { label: "40'HC Reefer" },
    { label: "Swapbody" }
];

const truckType = [
    { label: "Standard Truck" },
    { label: "Refrigerated Truck" },
    { label: "Container Truck" },
    { label: "Flatbed Truck" },
    { label: "Tanker Truck" },
    { label: "Lowboy Truck" },
    { label: "Dump Truck" },
    { label: "Car Carrier Truck" },
    { label: "Tow Truck" },
    { label: "Fire Truck" }
];
export default React.memo(function RfqFormDialog({
                                                     open,
                                                     onClose,

                                                     // ✅ controlled
                                                     form,
                                                     setForm,

                                                     onSubmit,
                                                     onConnectQuote,

                                                     customerOptions,
                                                     selectedCustomer,
                                                     setSelectedCustomer,
                                                     onNewCustomer,

                                                     salesRepOptions,

                                                     countryOptions,
                                                     getCountryLabel,
                                                     canonicalCountry,
                                                     findCountryOption,

                                                     originKind,
                                                     destinationKind,
                                                     originOptions,
                                                     destOptions,
                                                     originLoading,
                                                     destLoading,

                                                     STATUS_META,
                                                     StatusDot,
                                                 }) {
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((p) => (p[name] === value ? p : { ...p, [name]: value }));
    }, [setForm]);

    const handleCheckbox = useCallback((e) => {
        const { name, checked } = e.target;
        setForm((p) => ({ ...p, [name]: checked }));
    }, [setForm]);

    const handleSubmit = useCallback(() => onSubmit(form), [form, onSubmit]);
    const handleConnect = useCallback(() => onConnectQuote(form), [form, onConnectQuote]);
    const showDimensions = !["Road FTL", "Ocean FCL"].includes(form.transportationMode);
    const showContainerType = ["Ocean FCL", "Ocean LCL"].includes(form.transportationMode);
    const showTruckType = ["Road FTL", "Road LTL"].includes(form.transportationMode);
    const isClosed = String(form.status || "").toLowerCase() === "closed";

    const originLocationValue = useMemo(() => {
        if (originKind === "city") return form.originCity || null;
        return form.pol || null;
    }, [originKind, form.originCity, form.pol]);

    const destinationLocationValue = useMemo(() => {
        if (destinationKind === "city") return form.destinationCity || null;
        return form.pod || null;
    }, [destinationKind, form.destinationCity, form.pod]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Shipment Details – {form?.id ? "Update" : "Create"} Sent Request</span>

                {!!form?.rfqNumber && (
                    <TextField
                        label="RFQ Number"
                        value={form.rfqNumber}
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ width: 180 }}
                    />
                )}
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={9}>
                        <Autocomplete
                            value={selectedCustomer}
                            onChange={(_e, v) => setSelectedCustomer(v)}
                            options={customerOptions}
                            isOptionEqualToValue={(opt, val) => String(opt?.id || opt?._id) === String(val?.id || val?._id)}
                            getOptionLabel={(o) => o?.name || o?.label || o?.companyName || o?.customerName || o?.attributes?.name || ""}
                            renderInput={(params) => <TextField {...params} label="Customer" fullWidth />}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3} display="flex" alignItems="center">
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={onNewCustomer}
                            sx={{ height: 40, textTransform: "none", borderRadius: 2, fontWeight: 700 }}
                        >
                            New Customer
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            select fullWidth
                            label="Transportation Mode"
                            name="transportationMode"
                            value={form.transportationMode || ""}
                            onChange={handleChange}
                        >
                            <MenuItem value="Ocean FCL">Ocean FCL</MenuItem>
                            <MenuItem value="Ocean LCL">Ocean LCL</MenuItem>
                            <MenuItem value="Air">Air</MenuItem>
                            <MenuItem value="Road FTL">Road FTL</MenuItem>
                            <MenuItem value="Road LTL">Road LTL</MenuItem>
                            <MenuItem value="Project">Project</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select fullWidth
                            label="Service Mode"
                            name="serviceMode"
                            value={form.serviceMode || ""}
                            onChange={handleChange}
                        >
                            <MenuItem value="Door to Door">Door to Door</MenuItem>
                            <MenuItem value="Door to Port">Door to Port</MenuItem>
                            <MenuItem value="Port to Door">Port to Door</MenuItem>
                            <MenuItem value="Port to Port">Port to Port</MenuItem>
                        </TextField>
                    </Grid>



                    {/* Row 3 */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={countryOptions}
                            getOptionLabel={getCountryLabel}
                            value={findCountryOption(form.origin)}
                            onChange={(_e, val) => {
                                const label = val ? getCountryLabel(val) : "";
                                setForm((p) => ({ ...p, origin: canonicalCountry(label), originCity: "", pol: "" }));
                            }}
                            renderInput={(params) => <TextField {...params} label="Origin Country" fullWidth />}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            disabled={!form.origin || !originKind}
                            loading={originLoading}
                            options={originOptions || []}
                            value={originLocationValue}
                            onChange={(_e, val) => {
                                setForm((p) => (originKind === "city" ? { ...p, originCity: val || "" } : { ...p, pol: val || "" }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Origin"
                                    fullWidth
                                    helperText={!form.origin ? "Select Origin Country first" : ""}
                                />
                            )}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                        />
                    </Grid>

                    {/* Row 4 */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={countryOptions}
                            getOptionLabel={getCountryLabel}
                            value={findCountryOption(form.destination)}
                            onChange={(_e, val) => {
                                const label = val ? getCountryLabel(val) : "";
                                setForm((p) => ({ ...p, destination: canonicalCountry(label), destinationCity: "", pod: "" }));
                            }}
                            renderInput={(params) => <TextField {...params} label="Destination Country" fullWidth />}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            disabled={!form.destination || !destinationKind}
                            loading={destLoading}
                            options={destOptions || []}
                            value={destinationLocationValue}
                            onChange={(_e, val) => {
                                setForm((p) =>
                                    destinationKind === "city" ? { ...p, destinationCity: val || "" } : { ...p, pod: val || "" }
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Destination"
                                    fullWidth
                                    helperText={!form.destination ? "Select Destination Country first" : ""}
                                />
                            )}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                        />
                    </Grid>

                    {/* Cargo */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth multiline rows={2}
                            label="Cargo Description"
                            name="cargoDescription"
                            value={form.cargoDescription || ""}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* HS / Value / Unit */}
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="HS Code" name="hsCode" value={form.hsCode || ""} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Value of Cargo" name="valueOfCargo" value={form.valueOfCargo || ""} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Unit" name="valueOfCargoUnit" value={form.valueOfCargoUnit || ""} onChange={handleChange} />
                    </Grid>

                    {/* Insurance + Special Instructions */}
                    <Grid item xs={12} md={6} display="flex" alignItems="center">
                        <FormControlLabel
                            control={<Checkbox checked={!!form.insurance} onChange={handleCheckbox} name="insurance" />}
                            label="Insurance Required"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Special Instructions" name="specialInstructions" value={form.specialInstructions || ""} onChange={handleChange} />
                    </Grid>

                    {/* Weight + Volume */}
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Gross Weight (kg)" name="grossWeightKg" value={form.grossWeightKg || ""} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Volume (cbm)" name="volumeCbm" value={form.volumeCbm || ""} onChange={handleChange} />
                    </Grid>

                    {/* Dates */}
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth type="date" label="RFQ Requested Date" name="etd" InputLabelProps={{ shrink: true }} value={form.etd || ""} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth type="date" label="RFQ Deadline" name="eta" InputLabelProps={{ shrink: true }} value={form.eta || ""} onChange={handleChange} />
                    </Grid>

                    {/* Incoterm + Owner */}
                    <Grid item xs={12} md={6}>
                        <TextField select fullWidth label="Incoterm" name="incoterm" value={form.incoterm || ""} onChange={handleChange}>
                            {["CFR","CIF","CIP","CPT","DAP","DAT","DPU","DDP","EXW","FAS","FCA","FOB","FOT","FOR"].map((x) => (
                                <MenuItem key={x} value={x}>{x}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            multiple
                            options={salesRepOptions}
                            getOptionLabel={(o) => o.label}
                            value={salesRepOptions.filter((o) =>
                                (form.salesRepIds || []).some((id) => String(id) === String(o.id))
                            )}
                            onChange={(_e, vals) =>
                                setForm((p) => ({
                                    ...p,
                                    salesRepIds: vals.map((x) => String(x.id)),
                                }))
                            }
                            isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                            renderInput={(params) => <TextField {...params} label="RFQ Owner" fullWidth />}
                        />
                    </Grid>
                    {showContainerType && (
                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                options={containerType}
                                getOptionLabel={(option) => option.label}
                                value={
                                    containerType.find(
                                        (x) => x.label === form.equipmentType
                                    ) || null
                                }
                                onChange={(_e, value) =>
                                    setForm((p) => ({
                                        ...p,
                                        equipmentType: value?.label || "",
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Container Type"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {showTruckType && (
                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                options={truckType}
                                getOptionLabel={(option) => option.label}
                                value={
                                    truckType.find(
                                        (x) => x.label === form.equipmentType
                                    ) || null
                                }
                                onChange={(_e, value) =>
                                    setForm((p) => ({
                                        ...p,
                                        equipmentType: value?.label || "",
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Truck Type"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* Dimensions */}
                    {showDimensions && (
                        <>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Length (cm)"
                                    name="length"
                                    value={form.length || ""}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Width (cm)"
                                    name="width"
                                    value={form.width || ""}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Height (cm)"
                                    name="height"
                                    value={form.height || ""}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Note */}
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={2} label="Note" name="note" value={form.note || ""} onChange={handleChange} />
                    </Grid>

                    {/* Status / Offer / Currency */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            select size="small" fullWidth
                            label="Status"
                            name="status"
                            value={(form.status || "pending").toLowerCase()}
                            onChange={handleChange}
                            SelectProps={{
                                renderValue: (selected) => (
                                    <span style={{ display: "flex", alignItems: "center" }}>
                    <StatusDot status={selected} />
                                        {STATUS_META[selected]?.label || selected}
                  </span>
                                ),
                            }}
                        >
                            {Object.entries(STATUS_META).map(([k, meta]) => (
                                <MenuItem key={k} value={k}>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    <StatusDot status={k} /> {meta.label}
                  </span>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                  {/*  <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" label="Offer" name="offer" type="number" value={form.offer || ""} onChange={handleChange} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField select fullWidth size="small" label="Offer Currency" name="offerCurrency" value={form.offerCurrency || "USD"} onChange={handleChange}>
                            {["TRY","EUR","USD","GBP"].map((x) => (
                                <MenuItem key={x} value={x}>{x}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>*/}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="contained" color="inherit" sx={{ borderRadius: 2 }}>
                    CANCEL
                </Button>

                {isClosed && (
                    <Button onClick={handleConnect} variant="contained" sx={{ borderRadius: 2, bgcolor: "#ff7400", "&:hover": { bgcolor: "#ff7400" } }}>
                        CONNECT QUOTE
                    </Button>
                )}

                <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: 2 }}>
                    {form?.id ? "UPDATE" : "CREATE"}
                </Button>
            </DialogActions>
        </Dialog>
    );
});
